import React, { useState } from "react";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShieldHalved, faSearch } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

// ─────────────────────────────────────────────────────────────────────────────
//  Consent lookup — the DPDP question, answerable by a human.
//
//  ConsentRecord was written correctly from day one and readable only by a raw
//  Mongo query. A compliance ledger nobody can look up is evidence we cannot
//  actually produce: "what did this person agree to, in which language, on what
//  date" is a question that arrives by email with a deadline attached.
//
//  READ ONLY, PERMANENTLY. There is no edit, no delete, no "add consent" here,
//  and no endpoint behind one. A consent an administrator can author is not
//  consent, and a ledger an administrator can edit is not evidence.
//
//  The screen deliberately shows TWO things side by side: what was GRANTED (the
//  append-only records) and what is ENFORCED right now (the profile's live
//  scopes). They answer different questions, and a divergence between them is
//  precisely the thing worth investigating.
// ─────────────────────────────────────────────────────────────────────────────

const ConsentLookup = () => {
  const [phone, setPhone] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const lookup = async () => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) { toast.error("Enter a ten-digit mobile number"); return; }
    setLoading(true);
    setSearched(true);
    try {
      const res = await func.get(urls.PARTNER_CONSENT_URL, { params: { phone: digits } });
      if (res?.status === 200) setResult(res.data);
      else toast.error(res?.data?.message || "Lookup failed");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <h1 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
        <FontAwesomeIcon icon={faShieldHalved} className="text-amber-600" />
        Consent lookup
      </h1>
      <p className="text-sm text-gray-500 mt-1 mb-5">
        What a person agreed to, in which language, on what date. Read-only — consent
        cannot be created or edited from the admin panel.
      </p>

      <div className="flex gap-2 mb-6 max-w-md">
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && lookup()}
          placeholder="Mobile number"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
        />
        <button onClick={lookup} disabled={loading}
          className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2">
          <FontAwesomeIcon icon={faSearch} />
          {loading ? "Looking…" : "Look up"}
        </button>
      </div>

      {searched && !loading && result && (
        <>
          {result.note && <p className="text-sm text-gray-500 mb-4">{result.note}</p>}

          {result.liveScopes && (
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Enforced right now</h2>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-gray-500 uppercase">Scopes in force</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(result.liveScopes.consentScopes || []).length
                      ? result.liveScopes.consentScopes.map((s) => (
                          <span key={s} className="px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700">{s}</span>
                        ))
                      : <span className="text-gray-400 text-xs">none</span>}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase">Visibility / origin</div>
                  <div className="mt-1 text-gray-700 text-xs">
                    renderMode <b>{result.liveScopes.renderMode}</b> · source <b>{result.liveScopes.source}</b>
                    {result.liveScopes.originMatchmakerId ? " · brought by a bureau" : " · self-serve"}
                  </div>
                </div>
              </div>
            </div>
          )}

          <h2 className="text-sm font-semibold text-gray-700 mb-2">What was granted</h2>
          <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Granted</th>
                  <th className="text-left px-4 py-2">Purposes</th>
                  <th className="text-left px-4 py-2">Notice</th>
                  <th className="text-left px-4 py-2">How</th>
                  <th className="text-left px-4 py-2">Withdrawn</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(result.records || []).map((r) => (
                  <tr key={r._id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-700 text-xs">
                      {r.grantedAt ? new Date(r.grantedAt).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(r.purposes || []).map((p) => (
                          <span key={p} className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700">{p}</span>
                        ))}
                      </div>
                    </td>
                    {/* Version AND language: a Kannada notice and an English one
                        are different documents at the same version. */}
                    <td className="px-4 py-2 text-xs text-gray-600">
                      {r.noticeVersion} · {r.noticeLanguage}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600">
                      {r.channel}{r.assisted ? " · assisted" : ""}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {r.withdrawnAt
                        ? <span className="text-red-600">{new Date(r.withdrawnAt).toLocaleDateString()}</span>
                        : <span className="text-green-700">in force</span>}
                    </td>
                  </tr>
                ))}
                {!(result.records || []).length && (
                  <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-400">
                    No consent records on this number.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ConsentLookup;
